import React, { useState, useEffect } from 'react';

const FallSceneOverlay = () => {
  const [fallingLeaves, setFallingLeaves] = useState([]);

  useEffect(() => {
    // Generate serene autumn leaves
    const generateLeaves = () => {
      const leaves = [];
      for (let i = 0; i < 30; i++) {
        const depth = Math.random();
        leaves.push({
          id: i,
          left: Math.random() * 100,
          animationDuration: 18 + Math.random() * 25,
          animationDelay: Math.random() * 12,
          size: 8 + depth * 12,
          opacity: 0.4 + depth * 0.5,
          rotation: Math.random() * 360,
          leafType: Math.floor(Math.random() * 3),
          blur: (1 - depth) * 0.8,
        });
      }
      setFallingLeaves(leaves);
    };
    
    generateLeaves();
  }, []);

  return (
    <div className="fall-lakeside-overlay">
      <style jsx>{`
        .fall-lakeside-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
          pointer-events: none;
          overflow: hidden;
        }

        /* Misty autumn sky */
        .misty-fall-sky {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            to bottom,
            #8B9BA5 0%,
            #9BAAB3 20%,
            #A5B4BC 40%,
            #B8C5CC 60%,
            #CBD7DD 80%,
            #D4DFE5 100%
          );
          z-index: 0;
          opacity: 1;
        }

        /* Atmospheric mist layer */
        .fall-mist {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 70%;
          background: radial-gradient(
            ellipse at 50% 40%,
            rgba(200, 210, 220, 0.5) 0%,
            rgba(180, 190, 200, 0.35) 40%,
            transparent 75%
          );
          z-index: 1;
          opacity: 1;
          animation: mistDrift 20s ease-in-out infinite;
        }

        @keyframes mistDrift {
          0%, 100% { opacity: 0.7; transform: translateX(0); }
          50% { opacity: 0.9; transform: translateX(25px); }
        }

        /* Distant misty forest hills */
        .distant-fall-forest {
          position: absolute;
          bottom: 35%;
          left: 0;
          width: 100%;
          height: 40%;
          background: linear-gradient(
            to bottom,
            rgba(90, 100, 110, 0.25) 0%,
            rgba(80, 90, 100, 0.45) 50%,
            rgba(70, 80, 90, 0.65) 100%
          );
          z-index: 2;
          opacity: 1;
        }

        /* Autumn trees - left framing */
        .fall-trees-left {
          position: absolute;
          top: 0;
          left: 0;
          width: 35%;
          height: 70%;
          z-index: 3;
          opacity: 1;
        }

        /* Autumn trees - right framing */
        .fall-trees-right {
          position: absolute;
          top: 0;
          right: 0;
          width: 35%;
          height: 70%;
          z-index: 3;
          opacity: 1;
        }

        .fall-tree-trunk {
          position: absolute;
          bottom: 0;
          background: linear-gradient(
            90deg,
            #4A4A4A 0%,
            #5A5A5A 25%,
            #6A6A6A 50%,
            #5A5A5A 75%,
            #4A4A4A 100%
          );
          border-radius: 50% 50% 0 0;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.6);
        }

        .fall-foliage {
          position: absolute;
          border-radius: 50%;
          animation: gentleFallSway 12s ease-in-out infinite;
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
          opacity: 1;
        }

        @keyframes gentleFallSway {
          0%, 100% { transform: translateY(0) rotate(-0.5deg); }
          50% { transform: translateY(-6px) rotate(0.5deg); }
        }

        /* Rustic lakeside cabin centerpiece */
        .fall-cabin {
          position: absolute;
          bottom: 30%;
          left: 50%;
          transform: translateX(-50%);
          width: 200px;
          height: 180px;
          z-index: 4;
          opacity: 1;
        }

        .cabin-main-structure {
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 140px;
          height: 100px;
          background: linear-gradient(
            135deg,
            #4A3A2A 0%,
            #5C4A3A 50%,
            #4A3A2A 100%
          );
          box-shadow: 0 15px 45px rgba(0, 0, 0, 0.7);
        }

        .cabin-roof-fall {
          position: absolute;
          bottom: 85px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 85px solid transparent;
          border-right: 85px solid transparent;
          border-bottom: 60px solid #3A2A1A;
          filter: drop-shadow(0 10px 25px rgba(0, 0, 0, 0.6));
        }

        .cabin-glow-window {
          position: absolute;
          background: linear-gradient(
            135deg,
            #FFD580 0%,
            #FFC966 50%,
            #FFB84D 100%
          );
          border-radius: 3px;
          box-shadow: 
            0 0 25px rgba(255, 200, 100, 0.9),
            0 0 45px rgba(255, 180, 80, 0.6),
            inset 0 3px 8px rgba(255, 255, 255, 0.35);
          animation: windowWarmGlow 4s ease-in-out infinite;
        }

        @keyframes windowWarmGlow {
          0%, 100% { 
            box-shadow: 
              0 0 25px rgba(255, 200, 100, 0.8),
              0 0 45px rgba(255, 180, 80, 0.5);
          }
          50% { 
            box-shadow: 
              0 0 35px rgba(255, 200, 100, 1),
              0 0 55px rgba(255, 180, 80, 0.7);
          }
        }

        .cabin-entrance {
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 28px;
          height: 50px;
          background: linear-gradient(
            180deg,
            #3A2A1A 0%,
            #2A1A0A 100%
          );
          border-radius: 3px 3px 0 0;
          box-shadow: inset 0 3px 10px rgba(0, 0, 0, 0.7);
        }

        .cabin-chimney {
          position: absolute;
          bottom: 120px;
          right: 30px;
          width: 18px;
          height: 35px;
          background: linear-gradient(
            90deg,
            #5A4A3A 0%,
            #6A5A4A 50%,
            #5A4A3A 100%
          );
          border-radius: 2px 2px 0 0;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
        }

        .rising-smoke {
          position: absolute;
          top: -30px;
          left: 50%;
          transform: translateX(-50%);
          width: 25px;
          height: 40px;
          background: radial-gradient(
            ellipse at center,
            rgba(200, 200, 210, 0.7) 0%,
            rgba(180, 180, 190, 0.5) 40%,
            transparent 75%
          );
          border-radius: 50%;
          filter: blur(8px);
          animation: smokeDrift 6s ease-in-out infinite;
        }

        @keyframes smokeDrift {
          0% { 
            transform: translateX(-50%) translateY(0) scale(1);
            opacity: 0.8;
          }
          50% { 
            transform: translateX(-40%) translateY(-22px) scale(1.35);
            opacity: 0.5;
          }
          100% { 
            transform: translateX(-30%) translateY(-45px) scale(1.7);
            opacity: 0;
          }
        }

        /* Serene autumn lake */
        .fall-lake {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 35%;
          background: linear-gradient(
            to bottom,
            rgba(70, 80, 90, 0.8) 0%,
            rgba(60, 70, 80, 0.9) 50%,
            rgba(50, 60, 70, 1) 100%
          );
          z-index: 5;
          opacity: 1;
        }

        /* Lake reflection shimmer */
        .lake-reflection-fall {
          position: absolute;
          bottom: 35%;
          left: 0;
          width: 100%;
          height: 35%;
          background: linear-gradient(
            to top,
            rgba(70, 80, 90, 0.35) 0%,
            transparent 100%
          );
          z-index: 4;
          opacity: 1;
        }

        /* Gentle water ripples */
        .water-ripples {
          position: absolute;
          width: 100%;
          height: 100%;
          background: repeating-linear-gradient(
            0deg,
            transparent 0px,
            rgba(255, 255, 255, 0.04) 2px,
            transparent 4px,
            transparent 22px
          );
          animation: rippleDrift 9s linear infinite;
        }

        @keyframes rippleDrift {
          0% { transform: translateY(0); }
          100% { transform: translateY(22px); }
        }

        /* Shore rocks */
        .shore-rock {
          position: absolute;
          border-radius: 50%;
          background: linear-gradient(
            135deg,
            #7A7A7A 0%,
            #9A9A9A 40%,
            #6A6A6A 100%
          );
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.6);
          z-index: 6;
          opacity: 1;
        }

        /* Wooden dock */
        .fall-dock {
          position: absolute;
          bottom: 20%;
          left: 45%;
          width: 100px;
          height: 8px;
          background: linear-gradient(
            90deg,
            #6B5A4A 0%,
            #7B6A5A 50%,
            #6B5A4A 100%
          );
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.7);
          z-index: 6;
          opacity: 1;
          transform: perspective(500px) rotateX(15deg);
        }

        .dock-plank {
          position: absolute;
          top: 0;
          height: 100%;
          width: 2px;
          background: rgba(0, 0, 0, 0.35);
        }

        /* Graceful falling leaves */
        .serene-fall-leaf {
          position: absolute;
          animation: sereneFall linear infinite;
          filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.25));
          opacity: 1;
        }

        @keyframes sereneFall {
          0% {
            transform: translateY(-50px) rotate(0deg) translateX(0);
            opacity: 0.75;
          }
          50% {
            transform: translateY(50vh) rotate(180deg) translateX(45px);
            opacity: 0.55;
          }
          100% {
            transform: translateY(105vh) rotate(360deg) translateX(-35px);
            opacity: 0.15;
          }
        }

        .leaf-maple-fall {
          width: 100%;
          height: 100%;
          clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
        }

        /* Foreground ground layer */
        .fall-ground {
          position: absolute;
          bottom: 0;
          width: 100%;
          height: 10%;
          background: linear-gradient(
            to bottom,
            rgba(70, 60, 50, 0.7) 0%,
            rgba(60, 50, 40, 0.9) 50%,
            rgba(50, 40, 30, 1) 100%
          );
          z-index: 7;
          box-shadow: 0 -18px 45px rgba(50, 40, 30, 0.5);
          opacity: 1;
        }

        .scattered-ground-leaf {
          position: absolute;
          width: 12px;
          height: 10px;
          border-radius: 50% 0 50% 0;
          opacity: 0.55;
          transform: rotate(var(--rotation));
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .fall-trees-left,
          .fall-trees-right { width: 30%; }
          .fall-cabin { width: 150px; height: 140px; }
          .cabin-main-structure { width: 100px; height: 75px; }
          .fall-dock { width: 70px; }
        }
      `}</style>

      {/* Misty autumn sky */}
      <div className="misty-fall-sky" />

      {/* Atmospheric mist */}
      <div className="fall-mist" />

      {/* Distant misty forest */}
      <div className="distant-fall-forest" />

      {/* Left autumn trees */}
      <div className="fall-trees-left">
        {[
          { x: 15, trunk: 40, foliage: [
            { x: 10, y: 250, size: 100, color: '#FF8C42' },
            { x: 60, y: 280, size: 90, color: '#FFA559' },
            { x: 35, y: 320, size: 85, color: '#FFB366' }
          ]},
          { x: 45, trunk: 35, foliage: [
            { x: 35, y: 280, size: 95, color: '#FF9E4F' },
            { x: 80, y: 310, size: 88, color: '#FFAD66' }
          ]},
          { x: 75, trunk: 38, foliage: [
            { x: 60, y: 290, size: 92, color: '#FF8C42' },
            { x: 100, y: 320, size: 80, color: '#FFA559' }
          ]}
        ].map((tree, idx) => (
          <div key={`left-${idx}`}>
            <div 
              className="fall-tree-trunk"
              style={{
                left: `${tree.x}%`,
                width: `${tree.trunk}px`,
                height: '75%'
              }}
            />
            {tree.foliage.map((f, fIdx) => (
              <div
                key={fIdx}
                className="fall-foliage"
                style={{
                  left: `${f.x}%`,
                  bottom: `${f.y}px`,
                  width: `${f.size}px`,
                  height: `${f.size}px`,
                  background: `radial-gradient(circle at 35% 35%, ${f.color}, ${f.color}dd, ${f.color}aa)`,
                  animationDelay: `${idx * 0.7 + fIdx * 0.4}s`
                }}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Right autumn trees */}
      <div className="fall-trees-right">
        {[
          { x: 15, trunk: 38, foliage: [
            { x: 0, y: 270, size: 95, color: '#FF9E4F' },
            { x: 45, y: 300, size: 87, color: '#FFAD66' }
          ]},
          { x: 50, trunk: 42, foliage: [
            { x: 35, y: 260, size: 98, color: '#FF8C42' },
            { x: 80, y: 290, size: 90, color: '#FFA559' }
          ]},
          { x: 82, trunk: 36, foliage: [
            { x: 70, y: 280, size: 92, color: '#FFB366' },
            { x: 105, y: 310, size: 83, color: '#FF9E4F' }
          ]}
        ].map((tree, idx) => (
          <div key={`right-${idx}`}>
            <div 
              className="fall-tree-trunk"
              style={{
                right: `${tree.x}%`,
                width: `${tree.trunk}px`,
                height: '75%'
              }}
            />
            {tree.foliage.map((f, fIdx) => (
              <div
                key={fIdx}
                className="fall-foliage"
                style={{
                  right: `${f.x}%`,
                  bottom: `${f.y}px`,
                  width: `${f.size}px`,
                  height: `${f.size}px`,
                  background: `radial-gradient(circle at 35% 35%, ${f.color}, ${f.color}dd, ${f.color}aa)`,
                  animationDelay: `${idx * 0.7 + fIdx * 0.4 + 0.3}s`
                }}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Rustic lakeside cabin */}
      <div className="fall-cabin">
        <div className="cabin-roof-fall" />
        
        <div className="cabin-chimney">
          <div className="rising-smoke" style={{ animationDelay: '0s' }} />
          <div className="rising-smoke" style={{ animationDelay: '2s' }} />
          <div className="rising-smoke" style={{ animationDelay: '4s' }} />
        </div>
        
        <div className="cabin-main-structure">
          <div className="cabin-glow-window" style={{ 
            top: '20px', 
            left: '25px', 
            width: '28px', 
            height: '32px' 
          }} />
          <div className="cabin-glow-window" style={{ 
            top: '20px', 
            right: '25px', 
            width: '28px', 
            height: '32px',
            animationDelay: '1s'
          }} />
          
          <div className="cabin-entrance" />
        </div>
      </div>

      {/* Lake reflection */}
      <div className="lake-reflection-fall" />

      {/* Serene lake */}
      <div className="fall-lake">
        <div className="water-ripples" />
        
        {[
          { x: '42%', bottom: '35%', size: 45 },
          { x: '58%', bottom: '33%', size: 38 },
          { x: '48%', bottom: '28%', size: 32 }
        ].map((rock, i) => (
          <div
            key={i}
            className="shore-rock"
            style={{
              left: rock.x,
              bottom: rock.bottom,
              width: `${rock.size}px`,
              height: `${rock.size * 0.7}px`
            }}
          />
        ))}
      </div>

      {/* Wooden dock */}
      <div className="fall-dock">
        {[15, 30, 45, 60, 75, 90].map((pos) => (
          <div
            key={pos}
            className="dock-plank"
            style={{ left: `${pos}%` }}
          />
        ))}
      </div>

      {/* Graceful falling leaves */}
      {fallingLeaves.map((leaf) => (
        <div
          key={leaf.id}
          className="serene-fall-leaf"
          style={{
            left: `${leaf.left}%`,
            top: '-50px',
            width: `${leaf.size}px`,
            height: `${leaf.size}px`,
            animationDuration: `${leaf.animationDuration}s`,
            animationDelay: `${leaf.animationDelay}s`,
            filter: `blur(${leaf.blur}px) drop-shadow(0 2px 5px rgba(0, 0, 0, 0.25))`,
            opacity: leaf.opacity,
          }}
        >
          <div
            className="leaf-maple-fall"
            style={{
              background: [
                'linear-gradient(135deg, #FF8C42 0%, #FF7733 100%)',
                'linear-gradient(135deg, #FFA559 0%, #FF9544 100%)',
                'linear-gradient(135deg, #FFB366 0%, #FFA352 100%)',
              ][leaf.leafType],
              transform: `rotate(${leaf.rotation}deg)`,
            }}
          />
        </div>
      ))}

      {/* Foreground ground with scattered leaves */}
      <div className="fall-ground">
        {Array.from({ length: 20 }, (_, i) => (
          <div
            key={i}
            className="scattered-ground-leaf"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 60}%`,
              '--rotation': `${Math.random() * 360}deg`,
              background: [
                'linear-gradient(135deg, #FF8C42 0%, #FF7733 100%)',
                'linear-gradient(135deg, #FFA559 0%, #FF9544 100%)',
                'linear-gradient(135deg, #FFB366 0%, #FFA352 100%)',
              ][i % 3],
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default FallSceneOverlay;
